(ns ui.components.drop-tray.view
  "The drop tray of ZapKit: a tray at the bottom of a board with the targets that
   set the outcome of a dragged card. It follows `DropTray.tsx`. The classes come
   from the generated `ui.components.drop-tray.zapkit-styles`. The tray does not
   know when it opens or what a drop does. The caller shows it while a card is
   dragged, marks the target that the card is over (`:active?`) or that takes the
   drop (`:absorbing?`), and handles the drop. A target takes the props of a
   `div`, so the caller can pass `:on-drag-over`, `:on-drag-leave` and `:on-drop`."
  (:require
   [common.localization :as loc]
   [ui.components.drop-tray.zapkit-styles :as styles]
   [ui.components.zapkit-variants :as variants]
   [ui.icons :as icons]
   [ui.util :as uu]))

(defn target-state
  "The state of a target: `:absorbing` while the drop settles on it, `:active` while
   the card is over it, and `:rest` otherwise."
  [{:keys [active? absorbing?]}]
  (cond
    absorbing? :absorbing
    active? :active
    :else :rest))

(defn- target
  [{:keys [target-slot label-slot icon-slot icon default-label]}
   {:keys [active? absorbing? label class] :as props}]
  (let [attrs (dissoc props :active? :absorbing? :label :class)
        highlighted? (boolean (or active? absorbing?))]
    [:div
     (assoc attrs
            :class (uu/cm (variants/slot-class target-slot {:state (target-state props)})
                          class))
     [icon
      {:aria-hidden true
       :class       (:base icon-slot)}]
     [:span
      {:class (variants/slot-class label-slot {:active highlighted?})}
      (or label default-label)]]))

(defn on-hold-target
  "The target that puts a card on hold. Props: `:label` (the text, \"Put on hold\"
   by default), `:active?`, `:absorbing?`, `:class` and the props of a `div`."
  [props]
  [target
   {:target-slot   (:on-hold styles/drop-tray)
    :label-slot    (:on-hold-label styles/drop-tray)
    :icon-slot     (:on-hold-icon styles/drop-tray)
    :icon          icons/pause-icon
    :default-label (loc/tr :put-on-hold)}
   props])

(defn lost-target
  "The target that marks a card as lost. Props: `:label` (the text, \"Mark as lost\"
   by default), `:active?`, `:absorbing?`, `:class` and the props of a `div`."
  [props]
  [target
   {:target-slot   (:lost styles/drop-tray)
    :label-slot    (:lost-label styles/drop-tray)
    :icon-slot     (:lost-icon styles/drop-tray)
    :icon          icons/ban-icon
    :default-label (loc/tr :drop-tray-mark-lost)}
   props])

(defn view
  "The tray. `:message` is the help line above the targets. It is not shown when
   it is nil. The children are the targets. The tray is a column with a fixed
   height of 116 px, which is one row of targets under the message. Put the
   targets in one row, `[:div {:class \"tw2-flex tw2-w-full tw2-gap-3\"} ...]`, as
   the ZapKit story does. `:class` and the props of a `section` are passed on."
  [{:keys [message class] :as props} & children]
  (let [attrs (dissoc props :message :class)]
    (into [:section
           (assoc attrs
                  :class (uu/cm (variants/slot-class (:root styles/drop-tray) {})
                                class))
           (when message
             [:p {:class (variants/slot-class (:message styles/drop-tray) {})}
              message])]
          children)))
